import React, { useMemo } from 'react';
import { Cards, User } from '@phosphor-icons/react';
import type { ScriptBlockData } from './ScriptBlock';
import { speakerName } from './scriptEditing';
import { formatScriptPage } from '../../lib/scriptOutline';

// Index Cards: one card per scene of the screenplay (Final Draft's Index Cards view).
// Click a card to jump to the scene.

interface SceneCard {
    blockId: string;
    number: number | string;
    heading: string;
    summary: string;
    page: number | undefined;
    characters: string[];
}

interface SceneCardsProps {
    blocks: ScriptBlockData[];
    startPages: Map<string, number>;
    sceneNumbers: Record<string, number | string>;
    onOpenScene: (blockId: string) => void;
}

// Character cues in a block, including both sides of dual dialogue
const cuesIn = (block: ScriptBlockData): string[] => {
    if (block.type === 'character') return [block.content];
    if (block.type === 'dual-dialogue' && block.dualDialogue) {
        return [...block.dualDialogue.left, ...block.dualDialogue.right]
            .filter(paragraph => paragraph.type === 'character')
            .map(paragraph => paragraph.content);
    }
    return [];
};

export const SceneCards: React.FC<SceneCardsProps> = ({ blocks, startPages, sceneNumbers, onOpenScene }) => {
    const cards = useMemo(() => {
        const result: SceneCard[] = [];
        let current: SceneCard | null = null;
        for (const block of blocks) {
            if (block.type === 'scene-heading') {
                current = {
                    blockId: block.id,
                    number: sceneNumbers[block.id] ?? result.length + 1,
                    heading: block.content.trim() || 'Untitled scene',
                    summary: '',
                    page: startPages.get(block.id),
                    characters: []
                };
                result.push(current);
            }
            if (!current) continue;
            if (!current.summary && block.type === 'action' && block.content.trim()) current.summary = block.content.trim();
            for (const cue of cuesIn(block)) {
                const name = speakerName(cue).trim();
                if (name && !current.characters.includes(name)) current.characters.push(name);
            }
        }
        return result;
    }, [blocks, startPages, sceneNumbers]);

    if (cards.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center">
                <Cards className="w-10 h-10 text-zinc-600 mb-3" />
                <p className="text-sm text-zinc-300">No scenes yet</p>
                <p className="text-xs text-zinc-500 mt-1">Index cards show one card per scene heading.</p>
            </div>
        );
    }

    return (
        <div className="grid gap-3 p-6 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
            {cards.map(card => (
                <button
                    key={card.blockId}
                    onClick={() => onOpenScene(card.blockId)}
                    className="text-left flex flex-col min-h-[150px] rounded-md border border-white/10 bg-zinc-900 hover:border-indigo-400/50 hover:bg-zinc-800/80 transition-colors overflow-hidden"
                    title="Open this scene in the screenplay"
                >
                    <div className="flex items-start gap-2 px-3 pt-2.5">
                        <span className="text-[10px] font-mono text-zinc-500 pt-0.5">{card.number}</span>
                        <span className="flex-1 text-[11px] font-mono font-semibold uppercase text-zinc-100 leading-snug line-clamp-2">{card.heading}</span>
                        {card.page !== undefined && <span className="text-[10px] italic text-zinc-500 whitespace-nowrap pt-0.5">p{formatScriptPage(card.page)}</span>}
                    </div>
                    <p className="px-3 pt-1.5 text-[12px] text-zinc-400 leading-snug line-clamp-3 flex-1">
                        {card.summary || <span className="text-zinc-600">No action yet</span>}
                    </p>
                    {card.characters.length > 0 && (
                        <div className="flex flex-wrap gap-1 px-3 py-2 border-t border-white/5">
                            {card.characters.map(name => (
                                <span key={name} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium text-entity-soft bg-entity/10">
                                    <User className="w-2.5 h-2.5" weight="fill" />
                                    {name}
                                </span>
                            ))}
                        </div>
                    )}
                </button>
            ))}
        </div>
    );
};
